import React, { useEffect, useState } from 'react';
import { Layout } from "@/components/Layout/Layout";
import { CommonCard } from '@/components/common/CommonCard';
import { SectionCard } from '@/components/common/SectionCard';
import { formatRand, GetFilterUrl } from '@/utility/helper';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IOrderResponse, IOrder } from '@/interfaces/Order/order.interface';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
    TrendingUp, Clock, ShoppingBag, DollarSign, CheckCircle2, RotateCcw, AlertTriangle, Package, Eye, X,
    BarChartHorizontal
} from 'lucide-react';
import { EmptyChart } from '@/components/common/EmptyChart';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Range } from 'react-date-range';
import DateRangePicker from '@/components/common/DateRange';
import { CommonSelect } from '@/components/common/CommonSelect';
import { RoutesLink } from '@/utility/RouteLinks';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { useActiveRole } from '@/hooks/useActiveRole';
import {
    CommonCardSkeleton, BarChartSkeleton, RecentOrdersSkeleton
} from '@/components/ui/Loaders/DashboardSkeletons';
import { useAppSelector } from '@/redux/store';

// ─── Store Manager Dashboard data interface ──────────────────────────────
interface IStoreDashboardData {
    counts: {
        totalEarnings: number; pendingPayments: number; totalOrders: number;
        requestedOrders: number; activeOrders: number; completedOrders: number;
        cancelledOrders: number; rejectedOrders: number; delayedOrders: number;
        onlineTime: number; pendingReturnRequest: number;
        totalProducts?: number; lowStockProducts?: number;
    };
    earningsChart: { date: string; earning: number }[];
    lowStockProducts?: ILowStockProduct[];
}

export interface ILowStockProduct {
    id: string;
    name: string;
    price: string;
    productInfo: {
        stockQuantity: number;
    };
    category?: { name: string };
    media?: { url: string }[];
}

export function StoreDashboardView() {
    const navigate = useRoleNavigate();
    const user = useAppSelector((state) => state.user.user);
    const storeName = user?.storeProfile?.name || 'Store';

    const [activeFilter, setActiveFilter] = useState<string>('');
    const [customDateRange, setCustomDateRange] = useState<Range[]>([{
        startDate: new Date(), endDate: new Date(), key: 'selection'
    }]);

    const [recentOrders, setRecentOrders] = useState<IOrder[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [storeData, setStoreData] = useState<IStoreDashboardData | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setOrdersLoading(true);

                const orderParams = GetFilterUrl({
                    limit: 5,
                    page: 1,
                    sort: "newest"
                });

                const dashboardParams = GetFilterUrl({
                    ...(activeFilter && activeFilter !== 'custom' ? { filter: activeFilter } : {}),
                    ...(activeFilter === 'custom' && customDateRange[0].startDate && customDateRange[0].endDate ? {
                        filter: 'custom',
                        startDate: customDateRange[0].startDate.toISOString().split('T')[0],
                        endDate: customDateRange[0].endDate.toISOString().split('T')[0],
                    } : {})
                });

                const ordersPromise = callApi<IOrderResponse>(
                    `${API_URL}${API_ENDPOINT.orders}?${orderParams}`, { method: 'GET' }
                );

                const storePromise = callApi<{ data: IStoreDashboardData }>(
                    `${API_URL}${API_ENDPOINT.storeDashboard}?${dashboardParams}`, { method: 'GET' }
                );

                const [ordersResponse, storeResponse] = await Promise.all([
                    ordersPromise, storePromise
                ]);

                if (ordersResponse.data?.order) {
                    setRecentOrders(ordersResponse.data.order.slice(0, 5));
                }
                if (storeResponse?.data) {
                    setStoreData(storeResponse.data);
                }
            } catch (error) {
                console.error("Store Dashboard fetch error:", error);
            } finally {
                setOrdersLoading(false);
            }
        };

        fetchData();
    }, [activeFilter, customDateRange[0]?.startDate, customDateRange[0]?.endDate]);

    const currentDate = new Date().toLocaleDateString('en-ZA', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    return (
        <Layout>
            <div className="space-y-6 pb-20 animate-in fade-in duration-500">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">
                            {storeName} <span className="font-medium text-muted-foreground">Dashboard</span>
                        </h1>
                        <p className="text-sm text-muted-foreground">Yebo Eggs · {currentDate}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                        <CommonSelect
                            value={activeFilter}
                            onValueChange={(val) => setActiveFilter(val as string)}
                            placeholder="Select Range"
                            options={[
                                { value: 'today', label: 'Today' },
                                { value: 'weekly', label: 'This Week' },
                                { value: 'monthly', label: 'This Month' },
                                { value: 'yearly', label: 'This Year' },
                                { value: 'custom', label: 'Custom Range' },
                            ]}
                        />
                        {activeFilter === 'custom' && (
                            <div className="animate-in slide-in-from-right-2 duration-200">
                                <DateRangePicker dateRange={customDateRange} setDateRange={setCustomDateRange} />
                            </div>
                        )}
                        {activeFilter && (
                            <button
                                onClick={() => setActiveFilter('')}
                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 rounded-md transition-colors border border-transparent hover:border-destructive/10 cursor-pointer shrink-0"
                            >
                                <X className="w-4 h-4" />
                                Clear Filter
                            </button>
                        )}
                    </div>
                </div>

                {/* Store KPI Cards */}
                {ordersLoading || !storeData ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <CommonCardSkeleton key={i} />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
                        <CommonCard label="Total Earnings" value={formatRand(storeData?.counts.totalEarnings || 0)} icon={<DollarSign className="w-5 h-5" />} iconColor="#10B981" onClick={() => navigate(RoutesLink.Orders)} />
                        <CommonCard label="Pending Payments" value={formatRand(storeData?.counts.pendingPayments || 0)} icon={<DollarSign className="w-5 h-5 text-warning" />} iconColor="#F59E0B" onClick={() => navigate(RoutesLink.Orders)} />
                        <CommonCard label="Total Orders" value={storeData?.counts.totalOrders || 0} icon={<ShoppingBag />} iconColor="#3B82F6" onClick={() => navigate(RoutesLink.Orders)} />
                        <CommonCard label="Pending Orders" value={storeData?.counts.requestedOrders || 0} icon={<ShoppingBag className="text-warning" />} iconColor="#F59E0B" onClick={() => navigate(RoutesLink.Orders)} />
                        {/* <CommonCard label="Active Orders" value={storeData?.counts.activeOrders || 0} icon={<Clock />} iconColor="#8B5CF6" onClick={() => navigate(RoutesLink.Orders)} /> */}
                        {/* <CommonCard label="Completed" value={storeData?.counts.completedOrders || 0} icon={<CheckCircle2 />} iconColor="#10B981" onClick={() => navigate(RoutesLink.Orders)} /> */}
                        {/* <CommonCard label="Cancelled" value={storeData?.counts.cancelledOrders || 0} icon={<ShoppingBag className="text-destructive" />} iconColor="#EF4444" onClick={() => navigate(RoutesLink.Orders)} /> */}
                        {/* <CommonCard label="Rejected" value={storeData?.counts.rejectedOrders || 0} icon={<ShoppingBag className="text-destructive" />} iconColor="#EF4444" onClick={() => navigate(RoutesLink.Orders)} /> */}
                        {/* <CommonCard label="Delayed" value={storeData?.counts.delayedOrders || 0} icon={<Clock className="text-warning" />} iconColor="#F59E0B" onClick={() => navigate(RoutesLink.Orders)} /> */}
                        <CommonCard label="Pending Returns" value={storeData?.counts.pendingReturnRequest || 0} icon={<RotateCcw className="text-warning" />} iconColor="#F59E0B" onClick={() => navigate(RoutesLink.Return)} />
                        <CommonCard label="Total Products" value={storeData?.counts.totalProducts || 0} icon={<Package className="w-5 h-5" />} iconColor="#3B82F6" onClick={() => navigate(RoutesLink.Products)} />
                        <CommonCard label="Low Stock" value={storeData?.counts.lowStockProducts ?? (storeData?.lowStockProducts?.length || 0)} icon={<AlertTriangle className="w-5 h-5" />} iconColor="#EF4444" onClick={() => navigate(RoutesLink.Products)} />
                        <CommonCard label="Online Time (Hrs)" value={storeData?.counts.onlineTime || 0} icon={<Clock className="text-primary" />} iconColor="#3B82F6" />
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <SectionCard title="Earnings Trend" icon={<TrendingUp className="w-5 h-5 text-primary" />}>
                            {ordersLoading || !storeData ? (
                                <BarChartSkeleton />
                            ) : storeData?.earningsChart?.length === 0 ? (
                                <EmptyChart />
                            ) : (
                                <div className="h-[350px] w-full pt-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={storeData?.earningsChart?.map((item: any) => {
                                                let label = '';
                                                if (activeFilter === 'today' && item.hour !== undefined) {
                                                    const hr = parseInt(item.hour, 10);
                                                    if (!isNaN(hr)) {
                                                        const ampm = hr >= 12 ? 'PM' : 'AM';
                                                        const displayHr = hr % 12 === 0 ? 12 : hr % 12;
                                                        label = `${displayHr} ${ampm}`;
                                                    } else {
                                                        label = `${item.hour}:00`;
                                                    }
                                                } else {
                                                    const dateStr = item.date || item.month;
                                                    const date = dateStr ? new Date(dateStr) : null;
                                                    if (date && !isNaN(date.getTime())) {
                                                        if (activeFilter === 'yearly') {
                                                            label = date.toLocaleDateString('en-US', { month: 'short' });
                                                        } else {
                                                            label = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
                                                        }
                                                    } else {
                                                        label = item.hour ? `${item.hour}:00` : '';
                                                    }
                                                }
                                                return { ...item, displayDate: label };
                                            }) || []}
                                            margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                                            <XAxis dataKey="displayDate" axisLine={false} tickLine={false} tick={{ fill: 'currentColor', opacity: 0.5, fontSize: 11 }} dy={10} interval={0} angle={-45} textAnchor="end" height={60} />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fill: 'currentColor', opacity: 0.5, fontSize: 12 }} tickFormatter={(value) => `R${value}`} />
                                            <Tooltip
                                                cursor={{ fill: 'hsl(var(--secondary))' }}
                                                contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))', borderRadius: '8px' }}
                                                formatter={(value) => [`R${value}`, 'Earning']}
                                            />
                                            <Bar dataKey="earning" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </SectionCard>
                    </div>

                    <div className="lg:col-span-1">
                        <SectionCard title="Recent Live Orders" icon={<Clock className="w-5 h-5 text-primary" />}>
                            <div className="space-y-4">
                                {ordersLoading ? (
                                    <RecentOrdersSkeleton />
                                ) : recentOrders.length > 0 ? (
                                    recentOrders.map((order, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-border/50 bg-secondary/20 hover:bg-secondary/40 transition-colors cursor-pointer" onClick={() => navigate(RoutesLink.ViewOrders.replace(':id', order.id))}>
                                            <div className="space-y-1">
                                                <p className="text-sm font-semibold">{order.orderId}</p>
                                                <p className="text-xs text-muted-foreground truncate max-w-[120px]">{order.user?.fullName || "Guest"}</p>
                                            </div>
                                            <div className="flex flex-col items-end gap-1.5">
                                                <span className="font-bold text-sm tracking-tight">R{order.finalAmount || order.totalAmount}</span>
                                                <StatusBadge status={order.status} />
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-center text-muted-foreground pt-4">No recent orders found.</p>
                                )}
                            </div>
                        </SectionCard>
                    </div>
                </div>

                {/* Low Stock Products Section */}
                {!ordersLoading && storeData?.lowStockProducts && storeData.lowStockProducts.length > 0 && (
                    <SectionCard
                        title="Low Stock Products"
                        icon={<AlertTriangle className="w-5 h-5 text-destructive" />}
                        extra={
                            <div
                                className="flex items-center gap-1.5 text-sm font-semibold text-primary cursor-pointer hover:underline"
                                onClick={() => navigate(RoutesLink.Products)}
                            >
                                <span>View All</span>
                                <Eye className="w-4 h-4" />
                            </div>
                        }
                    >
                        <div className="mt-2">
                            <CommonTable
                                data={storeData.lowStockProducts}
                                columns={[
                                    {
                                        key: 'product',
                                        header: 'Product',
                                        render: (item: ILowStockProduct) => (
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 shrink-0 rounded-lg bg-secondary/30 flex items-center justify-center overflow-hidden border border-border/40">
                                                    {item.media && item.media.length > 0 ? (
                                                        <img src={`${API_URL}/${item.media[0].url}`} alt={item.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <Package className="w-4 h-4 text-muted-foreground" />
                                                    )}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-foreground text-sm">{item.name}</span>
                                                    <span className="text-xs text-muted-foreground">{item.category?.name || 'Uncategorized'}</span>
                                                </div>
                                            </div>
                                        )
                                    },
                                    {
                                        key: 'price',
                                        header: 'Price',
                                        render: (item: ILowStockProduct) => (
                                            <span className="font-bold text-foreground text-sm">{formatRand(item.price)}</span>
                                        )
                                    },
                                    {
                                        key: 'stock',
                                        header: 'Stock Left',
                                        render: (item: ILowStockProduct) => (
                                            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold bg-destructive/10 text-destructive border border-destructive/20">
                                                {item.productInfo?.stockQuantity} Left
                                            </span>
                                        )
                                    }
                                ]}
                            />
                        </div>
                    </SectionCard>
                )}
            </div>
        </Layout>
    );
}
